import React from 'react';
import { useAuth } from '../services/contexts/AuthContext';
import { 
  Users, 
  UserCheck, 
  Building2, 
  Settings, 
  Shield, 
  FileText, 
  Activity, 
  Calendar, 
  MessageSquare, 
  Send,
  Heart,
  TrendingUp,
  Database,
  Mail,
  Lock,
  AlertTriangle,
  BarChart3,
  Clock,
  UserCog,
  Stethoscope,
  Home,
  Phone,
  History,
  Zap
} from 'lucide-react';

const Sidebar = ({ activeView, setActiveView }) => {
  const { user } = useAuth();

  const menuItems = {
    system_admin: [
      { id: 'dashboard', label: 'Dashboard', icon: Home, color: 'text-brand-blue' },
      { id: 'tenants', label: 'Clinic Management', icon: Building2, color: 'text-brand-darkCyan' },
      { id: 'users', label: 'User Management', icon: Users, color: 'text-brand-cyan' },
      { id: 'patients', label: 'Global Patients', icon: UserCheck, color: 'text-va-primary-600' },
      { id: 'configurations', label: 'System Config', icon: Settings, color: 'text-va-neutral-600' },
      { id: 'security', label: 'Security & Auth', icon: Shield, color: 'text-va-error-600' },
      { id: 'audit', label: 'Audit Logs', icon: FileText, color: 'text-va-warning-600' },
      { id: 'email-templates', label: 'Email Templates', icon: Mail, color: 'text-brand-blue' },
      { id: 'system-health', label: 'System Health', icon: Activity, color: 'text-brand-cyan' },
    ],
    clinic_head: [
      { id: 'dashboard', label: 'Dashboard', icon: Home, color: 'text-brand-blue' },
      { id: 'patients', label: 'Patient Management', icon: UserCheck, color: 'text-brand-cyan' },
      { id: 'staff', label: 'Staff Management', icon: Users, color: 'text-va-primary-600' },
      { id: 'recordings', label: 'Recordings', icon: Activity, color: 'text-va-accent-600' },
      { id: 'analytics', label: 'Analytics', icon: BarChart3, color: 'text-va-health-600' },
      { id: 'settings', label: 'Settings', icon: Settings, color: 'text-va-neutral-600' },
    ],
    clinic_technician: [
      { id: 'dashboard', label: 'Dashboard', icon: Home, color: 'text-brand-blue' },
      { id: 'patients', label: 'Patient Management', icon: UserCheck, color: 'text-brand-cyan' },
      { id: 'recordings', label: 'Recordings', icon: Activity, color: 'text-va-accent-600' },
      { id: 'schedule', label: 'Schedule', icon: Calendar, color: 'text-va-health-600' },
      { id: 'communications', label: 'Communications', icon: MessageSquare, color: 'text-va-primary-600' },
      { id: 'settings', label: 'Settings', icon: Settings, color: 'text-va-neutral-600' },
    ],
    patient: [
      { id: 'dashboard', label: 'My Health', icon: Heart, color: 'text-va-error-500' },
      { id: 'latest-vitals', label: 'Latest Vitals', icon: Activity, color: 'text-brand-cyan' },
      { id: 'history', label: 'Health History', icon: History, color: 'text-brand-blue' },
      { id: 'trends', label: 'Health Trends', icon: TrendingUp, color: 'text-va-primary-600' },
      { id: 'recordings', label: 'My Recordings', icon: Calendar, color: 'text-va-warning-600' },
      { id: 'alerts', label: 'Health Alerts', icon: AlertTriangle, color: 'text-va-warning-600' },
      { id: 'profile', label: 'My Profile', icon: Settings, color: 'text-va-neutral-600' },
    ]
  };

  const currentMenuItems = menuItems[user?.role] || menuItems.clinic_technician;

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'system_admin': 'System Administrator',
      'clinic_head': 'Clinic Head',
      'clinic_technician': 'Clinic Technician',
      'patient': 'Patient Portal'
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      'system_admin': 'from-brand-blue to-brand-darkCyan',
      'clinic_head': 'from-brand-blue to-brand-cyan',
      'clinic_technician': 'from-brand-cyan to-va-health-600',
      'patient': 'from-va-error-500 to-va-warning-600'
    };
    return colorMap[role] || 'from-va-neutral-500 to-va-neutral-600';
  };

  return (
    <div className="sidebar-modern flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b border-va-neutral-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-brand-blue to-brand-darkCyan rounded-xl flex items-center justify-center">
            <Heart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-va-neutral-900">VitalAudio</h1>
            <p className="text-sm text-va-neutral-600">Healthcare System</p>
          </div>
        </div>
      </div>

      {/* User Profile Section */}
      <div className="p-4">
        <div className="card-glass p-4 rounded-2xl">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className={`w-12 h-12 bg-gradient-to-br ${getRoleColor(user?.role)} rounded-xl flex items-center justify-center shadow-lg`}>
                <span className="text-white font-bold text-lg">
                  {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                </span>
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-brand-cyan rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-va-neutral-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-va-neutral-500 truncate">
                {getRoleDisplayName(user?.role)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Navigation Menu */}
      <nav className="flex-1 px-4 space-y-1">
        {currentMenuItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={`sidebar-nav-item w-full ${
                isActive ? 'active' : 'text-va-neutral-700 hover:text-va-neutral-900'
              }`}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'text-brand-blue' : item.color} transition-colors`} />
              <span className="font-medium">{item.label}</span>
              {isActive && (
                <div className="ml-auto w-2 h-2 bg-brand-blue rounded-full"></div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-va-neutral-200">
        <div className="text-center">
          <p className="text-xs text-va-neutral-500">
            VitalAudio v1.0
          </p>
          <p className="text-xs text-va-neutral-400 mt-1">
            Healthcare System
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;